import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import classNames from 'classnames';

import ResponseChartWidgetBackground from 'src/assets/dashboard/ResponseChartWidgetBackground.svg?react';
import { numberOrDash } from 'src/helper/helper';
import ComparisonPill from 'src/components/dashboard/DashboardPage/components/ComparisonPill/ComparisonPill';
import ErrorOverlayWithRefetchButton from 'src/components/dashboard/DashboardPage/components/ErrorOverlayWithRefetchButton';

import { styles } from './index.styles';

export const RESPONSE_RATE_WIDGET_TEST_ID = 'response-rate-widget';
export const RESPONSE_RATE_BAR_TEST_ID = 'response-rate-bar';

type Aggregate = {
  totalValue?: number;
  relativeValue?: number | null;
  absoluteValue?: number | null;
};

export type AggregateComparison = Record<
  keyof Aggregate,
  { absoluteDifference: string; relativeDifference: string }
>;

interface BaseResponseRateWidgetProps {
  errorMsg?: string;
  loading: boolean;
  refetch: () => void;
  aggregate: Aggregate;
  comparisonValues?: AggregateComparison;
  className?: string;
  ariaId: string;
  name: string;
}

const BaseResponseRateWidget: FC<BaseResponseRateWidgetProps> = ({
  errorMsg,
  loading,
  refetch,
  aggregate: { totalValue, relativeValue, absoluteValue },
  comparisonValues,
  className,
  ariaId,
  name,
}) => {
  const { t } = useTranslation('dashboard');
  const { colors } = useTheme();

  if (loading || errorMsg) {
    return (
      <Box sx={{ position: 'relative' }}>
        <Stack spacing={1} direction="row" data-test-id="loading-response-rate">
          <Skeleton variant="text" width="20%" />
          <Skeleton variant="rectangular" width="20%" height={35} />
          <Skeleton variant="text" width="20%" />
          <Skeleton variant="text" width="20%" />
        </Stack>
        {errorMsg && (
          <ErrorOverlayWithRefetchButton
            errorMsg={errorMsg}
            refetch={refetch}
          />
        )}
      </Box>
    );
  }

  return (
    <Grid
      className={classNames('ResponseRateWidget-root', className)}
      container
      data-test-id={RESPONSE_RATE_WIDGET_TEST_ID}
      spacing={2}
      sx={styles.responseRateWidget}
    >
      <Grid
        className="ResponseRateWidget-blockWrap"
        container
        item
        md={true}
        spacing={1}
        sx={styles.blockWrap}
        xs={12}
      >
        <Grid item xs={6} md={true}>
          <Typography
            component="span"
            variant="h4"
            id={ariaId}
            sx={{ fontWeight: 'bold', wordBreak: 'break-all' }}
          >
            {name}:
          </Typography>
        </Grid>

        <Grid item xs={6} md={true} sx={{ overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              className="ResponseRateWidget-responseBackground"
              sx={styles.responseBackground}
            >
              <Box sx={{ height: '100%' }}>
                <ResponseChartWidgetBackground
                  height="100%"
                  width="auto"
                  fill={colors.neutral[200]}
                  clipPath={`inset(0 0 0 ${relativeValue ?? 0}%)`}
                />
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  height: '100%',
                }}
              >
                <ResponseChartWidgetBackground
                  data-test-id={RESPONSE_RATE_BAR_TEST_ID}
                  height="100%"
                  width="auto"
                  fill={colors.secondary[500]}
                  clipPath={`inset(0 ${100 - (relativeValue ?? 0)}% 0 0)`}
                />
              </Box>
            </Box>

            <Typography
              component="span"
              variant="h4"
              data-test-id="BaseResponseRate-ResponseRate"
              sx={{ fontWeight: 'bold' }}
            >
              {numberOrDash(relativeValue, true)}
            </Typography>

            {comparisonValues && (
              <ComparisonPill
                sx={{ ml: 0.5 }}
                value={comparisonValues.relativeValue.absoluteDifference}
              />
            )}
          </Box>
        </Grid>
      </Grid>

      <Grid
        className="ResponseRateWidget-blockWrap"
        container
        item
        md={true}
        spacing={1}
        sx={styles.blockWrap}
        xs={12}
      >
        <Grid item xs={6} md={true}>
          <Typography component="span" variant="h4" sx={{ fontWeight: 'bold' }}>
            {t('Labels.Invited')}:
          </Typography>
        </Grid>

        <Grid item xs={6} md={true}>
          <Stack direction="row">
            <Typography
              component="span"
              variant="h4"
              data-test-id="BaseResponseRate-Invited"
              sx={{ fontWeight: 'bold' }}
            >
              {numberOrDash(totalValue)}
            </Typography>

            {comparisonValues && (
              <ComparisonPill
                sx={{ ml: 0.5 }}
                value={comparisonValues.totalValue.absoluteDifference}
              />
            )}
          </Stack>
        </Grid>
      </Grid>
      <Grid
        className="ResponseRateWidget-blockWrap"
        container
        item
        md={true}
        spacing={1}
        sx={styles.blockWrap}
        xs={12}
      >
        <Grid item xs={6} md={true}>
          <Typography component="span" variant="h4" sx={{ fontWeight: 'bold' }}>
            {t('Labels.Respondents')}:
          </Typography>
        </Grid>

        <Grid item xs={6} md={true}>
          <Stack direction="row">
            <Typography
              component="span"
              variant="h4"
              data-test-id="BaseResponseRate-Resondents"
              sx={{ fontWeight: 'bold' }}
            >
              {numberOrDash(absoluteValue)}
            </Typography>

            {comparisonValues && (
              <ComparisonPill
                sx={{ ml: 0.5 }}
                value={comparisonValues.absoluteValue.absoluteDifference}
              />
            )}
          </Stack>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default BaseResponseRateWidget;
